import { DOCUMENT } from '@angular/common';
import { Component, ElementRef, Inject, ViewEncapsulation } from '@angular/core';
import { FormGroup, FormBuilder, Validators, AbstractControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { DialogPanelComponentContent } from '@toro/common/shared/models/dialogs/DialogPanelComponentContent';
import { UtilitiesService } from '@toro/common/shared/services/utilities.service';
import { StockActions } from '@toro/common/shared/state-management/stock/stock.action';
import { TrackingsService } from '../../../../../services/tracking.service';
import { RequestSinacorNoteAction } from '../../../../../state-management/report-sinacor/report-sinacor.action';
import { DialogsService } from '@toro/common/shared/services/dialogs.service';
import { filter } from 'rxjs/operators';
import { SuccessDialogComponent } from './dialogs/success-dialog/success-dialog.component';
import { AlertDialogComponent } from './dialogs/alert-dialog/alert-dialog.component';
import { ActionEventsEnum } from '@toro/common/shared/enum/tracking/action-events.enum';

@Component({
    selector: 'sinacor-note',
    templateUrl: './sinacor-note.component.html',
    styleUrls: ['./sinacor-note.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class SinacorNoteComponent extends DialogPanelComponentContent{
    public startDate: Date;
    public finalDate: Date;
    public formDatePicker: FormGroup;
    public isMobile: boolean;
    public brokerageNotesList: any[];
    public pending: boolean = false;

    public constructor(
        public formBuilder: FormBuilder,
        @Inject(MAT_DIALOG_DATA) public data,
        @Inject(DOCUMENT) private document: Document,
        public elementRef: ElementRef,
        private dialogReference: MatDialogRef<SinacorNoteComponent>,
        private router: Router,
        private tracking: TrackingsService,
        private utilitiesService: UtilitiesService,
        private store: Store<any>,
        private dialogsService: DialogsService
    ) {
        super(elementRef, true);
        this.document.body.classList.add('freeze');

        this.isMobile = this.utilitiesService.isMobile();
        this.formDatePicker = this.formBuilder.group({
            formDatePickerStartField: ['', Validators.required],
            formDatePickerFinalField: ['', Validators.required],
            stock: ['', Validators.required],
            future: ['', Validators.required],
        });

        this.initListeners();
        this.store.dispatch({ type: StockActions.getNoteListDate });
    }
    
    public initListeners = () => {
        this.initFormListener();
        this.initBrokerageNotesListener();
        this.initSinacorNoteDownloadListener();
    }

    public initFormListener = () => {
        this.formDatePicker
            .controls['formDatePickerStartField']
            .valueChanges
            .subscribe( value => {
                if(!this.formDatePicker.controls['formDatePickerFinalField'].value){
                    this.formDatePicker.controls['formDatePickerFinalField']
                        .setValue(value)
                }
                this.checkAvailableDateRange(this.formDatePicker.controls['formDatePickerStartField'])
            });

        this.formDatePicker
            .controls['formDatePickerFinalField']
            .valueChanges
            .subscribe(() => {
                this.checkAvailableDateRange(this.formDatePicker.controls['formDatePickerFinalField']);
        });
    }

    public checkAvailableDateRange(formDatePicker: AbstractControl) {
        if(formDatePicker.value && new Date(formDatePicker.value).getMonth() === new Date().getMonth()) {
                formDatePicker.setValue('');
                this.showAlertDateRange();
        }
    }

    public showAlertDateRange() {
        if(!this.formDatePicker.controls['formDatePickerStartField'].value && !this.formDatePicker.controls['formDatePickerFinalField'].value) {
            this.dialogsService.showComponentAsAsidePanel(AlertDialogComponent, true);
        }
    }
    
    public initBrokerageNotesListener = () => {
        this.store.select((brokerageNotesStore) => brokerageNotesStore.stocks.incomeDates)
            .pipe(
                filter((incomeDates) => incomeDates && incomeDates.length)
            )
            .subscribe((stockDates) => {
                if (stockDates) {
                    this.brokerageNotesList = stockDates.map( stockDate => this.getFormatedDate(stockDate.referenceDate));
                    this.filterDatePicker = this.filterDatePicker.bind(this);
                }
            });
    }

    public initSinacorNoteDownloadListener = () => {
        this.store
            .select( (sinacorNotesStore) => sinacorNotesStore.requestSinacorNote.requestedNote)
            .pipe(
                filter((requestedNote) => requestedNote)
            )
            .subscribe((requestedNote) => {
                this.tracking.sendMetricsToMixpanel(
                    ActionEventsEnum.USER_CLICKED, 
                    {
                        'element': '[Stocks] Sinacor Request',
                        'origin': 'Nota Sinacor',
                        'Ações (Bovespa)': !!this.formDatePicker.controls['stock'].value,
                        'BMF (Futuros)': !!this.formDatePicker.controls['future'].value,
                    }
                )
                this.pending = false;
                this.closeDialog();
                this.dialogsService.showComponentAsAsidePanel(SuccessDialogComponent, true);
            });
    }

    /**
     * Encerra componente, habilita overflow e destroi dialogReference
     */
     public closeDialog() {
        this.document.body.classList.remove('freeze');
        this.close(this.dialogReference, null);
        this.dialogReference = null;
        this.router.navigateByUrl('/meus-investimentos');
    }

    public isValidConfirmButton() {
        if (
            !!this.formDatePicker.controls['formDatePickerStartField'].value && 
            !!this.formDatePicker.controls['formDatePickerFinalField'].value && 
            (this.formDatePicker.controls['stock'].value === true || 
            this.formDatePicker.controls['future'].value === true)) {
                return true;
            }
        return false;
    }

    /**
     * Filtra datas disponíveis no calendário até o dia de ontem(-1)
     *
     * @param date
     */
     public filterDatePicker(date: Date) {
        
        const today = this.getFormatedDate(new Date());
        const currentDate = this.getFormatedDate(date);

        if(currentDate === today){
            return false;
        }
        
        let found: boolean;
        this.brokerageNotesList.forEach(dateItem => {
            if(currentDate === dateItem) 
                found = true;
        })
        
        return found && currentDate !== today;
    }

    public downloadSinacorNote() {

        const dateFrom = this.getFormatedDate(this.formDatePicker.controls['formDatePickerStartField'].value);
        const dateTo = this.getFormatedDate(this.formDatePicker.controls['formDatePickerFinalField'].value);
        const bovespaCheckbox = !!this.formDatePicker.controls['stock'].value;
        const bmfCheckbox = !!this.formDatePicker.controls['future'].value;


        const payload = {
            "markets": {
                "stock": bovespaCheckbox,
                "future": bmfCheckbox
            },
            "dateFrom": dateFrom,
            "dateTo": dateTo
        }
        this.pending = true;
        this.store.dispatch({type: RequestSinacorNoteAction.requestSinacorNote, payload: { ...payload }});
    }

    /**
     * Converte uma data para o formato esperado pelas rotas de brokeragenote (yyyy/MM/dd)
     *
     * @param date
     */
     public getFormatedDate(date: Date) {
        return new Date(date).toISOString()
            .split('T')[0]
            .split('-')
            .join('/');
    }
    
}
